import type { ScheduleResponse, ScheduledGame } from '../../shared/types';

/**
 * Fixture-list aggregation: who plays whom, how often, and how hard that was.
 *
 * Imports no React, like every other module in this directory, so the esbuild
 * harness can compile it and assert against the frozen 2025 season.
 *
 * Keyed on **roster_id** throughout, unlike `rivalries.ts` and `projections.ts`
 * which key on user_id. That is deliberate and safe: a schedule only ever
 * describes a single league year, and roster ids are stable within one. Nothing
 * here is ever summed across seasons.
 */

export type Outcome = 'W' | 'L' | 'T';

/** One team's view of one week. */
export interface Fixture {
  week: number;
  opponent: number;
  /** Null until the week is played -- never 0. */
  points: number | null;
  opponentPoints: number | null;
  /** Null while the fixture is still upcoming. */
  outcome: Outcome | null;
}

export interface TeamSchedule {
  rosterId: number;
  fixtures: Fixture[];
  played: number;
  remaining: number;
}

function outcomeOf(mine: number, theirs: number): Outcome {
  return mine > theirs ? 'W' : mine < theirs ? 'L' : 'T';
}

/** Both sides of every fixture, grouped by roster and ordered by week. */
export function buildFixtures(schedule: ScheduleResponse | null): Map<number, TeamSchedule> {
  const out = new Map<number, TeamSchedule>();
  if (!schedule) return out;

  const get = (rosterId: number): TeamSchedule => {
    const existing = out.get(rosterId);
    if (existing) return existing;
    const fresh: TeamSchedule = { rosterId, fixtures: [], played: 0, remaining: 0 };
    out.set(rosterId, fresh);
    return fresh;
  };

  // Seed from the roster directory rather than from the games, so a team with
  // no fixtures at all still appears instead of silently vanishing.
  for (const id of Object.keys(schedule.rosterOwners)) get(Number(id));

  for (const g of schedule.games) {
    for (const [me, foe, mine, theirs] of [
      [g.rosterA, g.rosterB, g.pointsA, g.pointsB],
      [g.rosterB, g.rosterA, g.pointsB, g.pointsA],
    ] as const) {
      const row = get(me);
      const settled = mine !== null && theirs !== null;
      row.fixtures.push({
        week: g.week,
        opponent: foe,
        points: mine,
        opponentPoints: theirs,
        outcome: settled ? outcomeOf(mine, theirs) : null,
      });
      if (settled) row.played += 1;
      else row.remaining += 1;
    }
  }

  for (const row of out.values()) row.fixtures.sort((a, b) => a.week - b.week);
  return out;
}

// --- Who you meet, and how many times --------------------------------------

const pairKey = (a: number, b: number) => (a < b ? `${a}|${b}` : `${b}|${a}`);

/**
 * How many times each pair of rosters meets in the regular season.
 *
 * Not a formality in this league. The generated schedule is a mirrored double
 * round-robin, so with nine opponents and fourteen weeks the mirror of a
 * late-season pairing lands in the playoff weeks and is overwritten by the
 * bracket. The result is that you play five opponents twice and four once --
 * measured on 2026, 25 pairs meet twice and 20 meet once. Which side of that
 * split an opponent falls on is a real, uneven feature of a season and nothing
 * in Sleeper's own UI surfaces it.
 */
export function meetingCounts(schedule: ScheduleResponse | null): Map<string, number> {
  const counts = new Map<string, number>();
  for (const g of schedule?.games ?? []) {
    const k = pairKey(g.rosterA, g.rosterB);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return counts;
}

export interface Meetings {
  /** Opponents faced twice, and those faced once. */
  twice: number[];
  once: number[];
}

export function meetingsFor(
  counts: Map<string, number>,
  rosterId: number,
  allRosterIds: number[],
): Meetings {
  const twice: number[] = [];
  const once: number[] = [];
  for (const other of allRosterIds) {
    if (other === rosterId) continue;
    const n = counts.get(pairKey(rosterId, other)) ?? 0;
    if (n >= 2) twice.push(other);
    else if (n === 1) once.push(other);
  }
  return { twice, once };
}

// --- Scoring and strength of schedule --------------------------------------

export interface TeamScoring {
  rosterId: number;
  games: number;
  pointsFor: number;
  /** Mean points per game played, or null with nothing played yet. */
  mean: number | null;
}

/** Each roster's own scoring so far, from the fixture list itself. */
export function teamScoring(schedule: ScheduleResponse | null): Map<number, TeamScoring> {
  const out = new Map<number, TeamScoring>();
  const get = (rosterId: number): TeamScoring => {
    const existing = out.get(rosterId);
    if (existing) return existing;
    const fresh: TeamScoring = { rosterId, games: 0, pointsFor: 0, mean: null };
    out.set(rosterId, fresh);
    return fresh;
  };

  for (const id of Object.keys(schedule?.rosterOwners ?? {})) get(Number(id));

  for (const g of schedule?.games ?? []) {
    if (g.pointsA === null || g.pointsB === null) continue;
    for (const [id, pts] of [
      [g.rosterA, g.pointsA],
      [g.rosterB, g.pointsB],
    ] as const) {
      const row = get(id);
      row.games += 1;
      row.pointsFor += pts;
    }
  }

  for (const row of out.values()) {
    row.mean = row.games === 0 ? null : Math.round((row.pointsFor / row.games) * 100) / 100;
  }
  return out;
}

export interface ScheduleStrength {
  rosterId: number;
  /** Mean level of opponents already faced. */
  playedOpp: number | null;
  /** Mean level of opponents still to come. */
  remainingOpp: number | null;
  /** Mean across the whole season's fixture list. */
  fullOpp: number | null;
  /** `fullOpp` minus the league's own mean. Positive is a harder draw. */
  vsLeague: number | null;
}

/**
 * Opponent quality, weighted by how often you actually play each opponent.
 *
 * `levels` is roster id -> expected points per week, supplied by the caller
 * rather than computed here. That indirection is the whole point: the obvious
 * implementation -- average the opponents' scoring so far -- returns nothing at
 * all until games have been played, so in an August preseason the entire table
 * renders as a column of em-dashes at exactly the moment people are most
 * curious about the schedule they just got. Passing in the same shrunk strength
 * estimate the odds page uses makes the column answerable from day one and
 * keeps the two pages telling one story.
 *
 * NOTE that no head-to-head exclusion is applied, and that is correct here
 * rather than an oversight. In a real sport, ranking a team by its opponents'
 * records has to discount the games those opponents played against the team
 * itself, because one side's result is the other side's mirror image. Fantasy
 * scores are not: what a roster puts up in a week is entirely independent of
 * who it was scheduled against. So an opponent's level is an unbiased measure
 * of their strength no matter how many times you faced them, and dropping those
 * games would only shrink the sample.
 */
export function scheduleStrength(
  schedule: ScheduleResponse | null,
  levels: Map<number, number | null>,
): Map<number, ScheduleStrength> {
  const fixtures = buildFixtures(schedule);
  const out = new Map<number, ScheduleStrength>();

  const all = [...levels.values()].filter((m): m is number => m !== null);
  const leagueMean = all.length === 0 ? null : all.reduce((a, b) => a + b, 0) / all.length;

  const round2 = (n: number) => Math.round(n * 100) / 100;
  const avg = (ids: number[]): number | null => {
    // An opponent with no level contributes no information; the alternative --
    // treating them as zero -- would make a schedule look trivially easy in
    // exact proportion to how little is known, which is the opposite of true.
    const vals = ids.map((id) => levels.get(id)).filter((m): m is number => m != null);
    return vals.length === 0 ? null : round2(vals.reduce((a, b) => a + b, 0) / vals.length);
  };

  for (const [rosterId, team] of fixtures) {
    const full = avg(team.fixtures.map((f) => f.opponent));
    out.set(rosterId, {
      rosterId,
      playedOpp: avg(team.fixtures.filter((f) => f.outcome !== null).map((f) => f.opponent)),
      remainingOpp: avg(team.fixtures.filter((f) => f.outcome === null).map((f) => f.opponent)),
      fullOpp: full,
      vsLeague: full === null || leagueMean === null ? null : round2(full - leagueMean),
    });
  }

  return out;
}

// --- Convenience -----------------------------------------------------------

/** Roster id -> mean points per game so far, in the shape `scheduleStrength` wants. */
export function scoringLevels(scoring: Map<number, TeamScoring>): Map<number, number | null> {
  return new Map([...scoring].map(([id, s]) => [id, s.mean]));
}

/** Every week number the fixture list covers, ascending. */
export function scheduleWeeks(schedule: ScheduleResponse | null): number[] {
  if (!schedule) return [];
  return Array.from({ length: schedule.regularSeasonWeeks }, (_, i) => i + 1);
}

/** The earliest week with nothing played, i.e. the next one up. */
export function nextUnplayedWeek(schedule: ScheduleResponse | null): number | null {
  const pending = (schedule?.games ?? []).filter((g) => g.pointsA === null);
  if (pending.length === 0) return null;
  return Math.min(...pending.map((g) => g.week));
}

export function gamesInWeek(schedule: ScheduleResponse | null, week: number): ScheduledGame[] {
  return (schedule?.games ?? []).filter((g) => g.week === week);
}
